<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, onUnmounted, ref, useId } from 'vue'
import { RouterLink } from 'vue-router'
import { CRAFTED_NOT_IN_PLAN_MESSAGE, isCraftedRefusal, type Role, type TaskCopyInput, type TaskRow } from '@/api'
import { assignableRoles } from '@/lib/taskRoles'

/*
 * Every field is the kit's (#1226): the name and slug are kit `Input`s, the role a kit `Select`, on a kit surface. The
 * form states no colour, radius, border or fill of its own.
 *
 * A copy names no team: a task belongs to a role, and every team where a member holds that role runs it.
 */

const props = defineProps<{
    source: { slug: string; name?: string; assigneeRole?: string | null }
    roles: Pick<Role, 'slug' | 'name'>[]
    disabled?: boolean
    createCopy: (body: TaskCopyInput) => Promise<TaskRow>
}>()
const emit = defineEmits<{
    copied: [task: TaskRow]
    cancel: []
    'busy-change': [busy: boolean]
}>()
const name = ref(`${(props.source.name || props.source.slug).slice(0, 193)} (copy)`)
const slug = ref(`${props.source.slug.slice(0, 75)}-copy`)
/** Empty keeps the source task's role; a slug runs the copy as that role, for its holders on whatever team they sit. */
const assigneeRole = ref('')
const teamRoles = computed(() => assignableRoles(props.roles))
/** A copy that keeps a role the company does not have has nobody to run it. */
const keptRoleMissing = computed(
    () =>
        !assigneeRole.value &&
        Boolean(props.source.assigneeRole) &&
        !teamRoles.value.some(role => role.slug === props.source.assigneeRole),
)
/**
 * The role list's "keep the source task's role" choice. An empty role means keep it, as it always has, but the kit
 * Select (Reka) refuses an item whose value is the empty string, so the list offers it under this value and
 * choosing it empties the role again. Role slugs are lowercase letters, digits and hyphens, so no role can be
 * mistaken for it.
 */
const KEEP_SOURCE_ROLE = ':same-as-the-original'
const keepSourceRoleLabel = computed(() =>
    props.source.assigneeRole ? `Same as the original (${props.source.assigneeRole})` : 'Same as the original',
)

/**
 * What each picker holds. Nothing chosen is held as no value at all, so the picker reads its placeholder and the
 * field it mirrors its choice on reads empty, as the native select it replaced did.
 */
const roleChoice = computed(() => assigneeRole.value || undefined)

/** A picker's choice as a string; nothing chosen is the empty string. */
function said(choice: unknown): string {
    return choice === null || choice === undefined ? '' : String(choice)
}

function chooseRole(choice: unknown): void {
    const roleSlug = said(choice)
    assigneeRole.value = roleSlug === KEEP_SOURCE_ROLE ? '' : roleSlug
}

/** The kit Select's trigger is a button, so it is named by the words beside it rather than wrapped in a label. */
const fieldId = useId()

const busy = ref(false)
const locked = computed(() => busy.value || props.disabled)
const error = ref<string | null>(null)
/** The plan refused the copy's Zero Human content (#449): the error then links to Billing. */
const planRefused = ref(false)
const ready = computed(() => Boolean(name.value.trim() && slug.value.trim()))
let disposed = false
onUnmounted(() => {
    disposed = true
})

async function copy(): Promise<void> {
    if (locked.value || !ready.value) return
    busy.value = true
    emit('busy-change', true)
    error.value = null
    planRefused.value = false
    let created: TaskRow | null = null
    try {
        created = await props.createCopy({
            name: name.value.trim(),
            slug: slug.value.trim(),
            ...(assigneeRole.value ? { assigneeRole: assigneeRole.value } : {}),
        })
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        planRefused.value = isCraftedRefusal(caught)
        if (planRefused.value) error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else if (/slug|already_exists|duplicate/i.test(code))
            error.value = 'This slug is already in use or is invalid. Choose another slug and try again.'
        else error.value = 'Could not copy this task. Your choices have been kept; try again.'
    } finally {
        busy.value = false
        if (!disposed) emit('busy-change', false)
    }
    if (created && !disposed) emit('copied', created)
}
</script>

<template>
    <ZhPanel as="form" class="space-y-4" aria-label="Copy task" @submit.prevent="copy">
        <div class="space-y-1">
            <ZhSectionHeading>Copy {{ source.name || source.slug }}</ZhSectionHeading>
            <p class="text-muted-foreground text-sm">Create an independent task from its current version. Your copy starts at v1, belongs to your enterprise, and can be edited and improved independently.</p>
        </div>
        <p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
        <RouterLink v-if="error && planRefused" to="/billing#plans">See plans on Billing</RouterLink>
        <fieldset :disabled="locked" class="space-y-4">
            <div class="grid gap-3 sm:grid-cols-2">
                <label class="grid gap-1 text-sm">New name<Input v-model="name" required maxlength="200" /></label>
                <label class="grid gap-1 text-sm">New slug<Input v-model="slug" required maxlength="80" pattern="[a-z0-9][a-z0-9\-]{0,79}" /><span class="text-muted-foreground text-xs">Use lowercase letters, numbers and hyphens. The slug must be unique in your enterprise.</span></label>
                <div class="grid gap-1 text-sm">
                    <span :id="`${fieldId}-role`">Role</span>
                    <Select :model-value="roleChoice" :disabled="locked" data-testid="task-copy-role-select" @update:model-value="chooseRole">
                        <SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-role`">
                            <SelectValue :placeholder="keepSourceRoleLabel" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem :value="KEEP_SOURCE_ROLE">{{ keepSourceRoleLabel }}</SelectItem>
                            <SelectItem v-for="role in teamRoles" :key="role.slug" :value="role.slug">{{ role.name }}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <p class="text-muted-foreground text-sm">The copy runs as its role: whoever holds the role runs it, and every team they sit on lists it.</p>
            <p v-if="keptRoleMissing" data-testid="task-copy-role-missing" class="text-muted-foreground text-sm">Your company has no {{ source.assigneeRole }} role. Pick one, or the copy will not run until someone holds it.</p>
            <div class="flex flex-wrap gap-2"><Button type="submit" size="sm" :disabled="locked || !ready">{{ busy ? 'Copying…' : 'Create copy' }}</Button><Button type="button" size="sm" variant="outline" :disabled="locked" @click="emit('cancel')">Cancel</Button></div>
        </fieldset>
    </ZhPanel>
</template>
